import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import service from '../../store/store/baseapiservice';

interface ChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentLanguage: string;
  selectedCategory?: { categoryId: number; categoryName: string; subCategoryId: number; subCategoryName: string } | null;
  onTicketCreated?: () => void;
}

export default function ChatDrawer({ isOpen, onClose, currentLanguage, selectedCategory: propSelectedCategory, onTicketCreated }: ChatDrawerProps) {
  // Get selected category from prop or localStorage
  const [selectedCategory, setSelectedCategory] = useState<{ categoryId: number; categoryName: string; subCategoryId: number; subCategoryName: string } | null>(null);
  const [chatMessages, setChatMessages] = useState<Array<{ text: string; timestamp: string; sender: 'bot' | 'user'; images?: string[] }>>([]);
  const [messageInput, setMessageInput] = useState('');
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [showPhotoUploadPrompt, setShowPhotoUploadPrompt] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showConfirmationForm, setShowConfirmationForm] = useState(false);
  const [locationWard, setLocationWard] = useState<{ wrd_id: number; wrd_nu?: number; wrd_nm: string } | null>(null);
  const [locationSachivalayam, setLocationSachivalayam] = useState<{ svm_id: number; svm_nm: string; svm_apfs_cd?: string } | null>(null);
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaId, setCaptchaId] = useState<number | null>(null);
  const [captchaImage, setCaptchaImage] = useState<string>('');
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false);
  const [userProblem, setUserProblem] = useState('');
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [showPhotoShareModal, setShowPhotoShareModal] = useState(false);
  const [photoDescription, setPhotoDescription] = useState('');
  const [photoShareFiles, setPhotoShareFiles] = useState<File[]>([]);
  const [photoSharePreviewUrls, setPhotoSharePreviewUrls] = useState<string[]>([]);
  const [isSubmittingPhotos, setIsSubmittingPhotos] = useState(false);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [createdTicketNumber, setCreatedTicketNumber] = useState<string>('');

  // Initialize category from prop or localStorage
  useEffect(() => {
    if (isOpen) {
      if (propSelectedCategory) {
        setSelectedCategory(propSelectedCategory);
      } else {
        // Try to get from localStorage
        const grvnceDtls = localStorage.getItem('grvnceDtls');
        if (grvnceDtls) {
          try {
            const parsed = JSON.parse(grvnceDtls);
            setSelectedCategory({
              categoryId: parsed.slctdCtgry || 0,
              categoryName: parsed.slctdCtgryNm || '',
              subCategoryId: parsed.slctdSbCtgry || 0,
              subCategoryName: parsed.slctdSbCtgryNm || ''
            });
          } catch (e) {
            console.error('Error parsing grvnceDtls:', e);
          }
        }
      }

      // Initialize chat messages
      const initialMessages = [
        {
          text: currentLanguage === 'en' 
            ? 'Welcome to RMC Support. Thank you for contacting us for support.' 
            : 'RMC మద్దతుకు స్వాగతం. మద్దతు కోసం మమ్మల్ని సంప్రదించినందుకు ధన్యవాదాలు.',
          timestamp: new Date().toLocaleString('en-IN', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          sender: 'bot' as const
        },
        {
          text: currentLanguage === 'en'
            ? 'Now we will be asking few short questions to process your request.'
            : 'ఇప్పుడు మేము మీ అభ్యర్థనను ప్రాసెస్ చేయడానికి కొన్ని చిన్న ప్రశ్నలు అడుగుతాము.',
          timestamp: new Date().toLocaleString('en-IN', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          sender: 'bot' as const
        },
        {
          text: currentLanguage === 'en'
            ? 'Please describe your problem'
            : 'దయచేసి మీ సమస్యను వివరించండి',
          timestamp: new Date().toLocaleString('en-IN', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          sender: 'bot' as const
        }
      ];
      setChatMessages(initialMessages);
    }
  }, [isOpen, propSelectedCategory, currentLanguage]);

  const handleSendMessage = () => {
    if (!messageInput.trim()) return;

    // Store user problem text
    setUserProblem(messageInput);

    // Add user message
    const userMessage = {
      text: messageInput,
      timestamp: new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }),
      sender: 'user' as const
    };

    setChatMessages(prev => [...prev, userMessage]);
    setMessageInput('');

    // Show location prompt after user sends a message
    setTimeout(() => {
      const botResponse = {
        text: currentLanguage === 'en'
          ? 'Please share the location of the Problem site/location.'
          : 'దయచేసి సమస్య స్థలం/స్థానం యొక్క స్థానాన్ని భాగస్వామ్యం చేయండి.',
        timestamp: new Date().toLocaleString('en-IN', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        }),
        sender: 'bot' as const
      };
      setChatMessages(prev => [...prev, botResponse]);
      setShowLocationPrompt(true);
    }, 1000);
  };

  const handleShareLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude: lat, longitude: lng } = position.coords;
          setLatitude(lat);
          setLongitude(lng);
          const locationText = `${lat}, ${lng}`;
          setShowLocationPrompt(false);

          // Try to auto-bind Ward and Sachivalayam using the same route as Bulk Garbage Collection
          try {
            const response: any = await service.get(`/open/chatbot/${lat}/${lng}/sachivalayam`);
            const locationData = response?.data;

            const ward = Array.isArray(locationData?.wrd) && locationData.wrd.length > 0 ? locationData.wrd[0] : null;
            const svm = Array.isArray(locationData?.svm) && locationData.svm.length > 0 ? locationData.svm[0] : null;

            if (ward?.wrd_id) {
              setLocationWard({ wrd_id: ward.wrd_id, wrd_nu: ward.wrd_nu, wrd_nm: ward.wrd_nm });
          } else {
            setLocationWard(null);
            }

            if (svm?.svm_id) {
              setLocationSachivalayam({ svm_id: svm.svm_id, svm_nm: svm.svm_nm, svm_apfs_cd: svm.svm_apfs_cd });
          } else {
            setLocationSachivalayam(null);
            }
          } catch (err: any) {
            console.error('Error fetching ward/sachivalayam by location:', err);
          // Location-based binding failed; user must re-share location later
            setLocationWard(null);
            setLocationSachivalayam(null);
          }
          
          // Add location message to chat
          const locationMessage = {
            text: currentLanguage === 'en'
              ? `Location shared: ${locationText}`
              : `స్థానం భాగస్వామ్యం చేయబడింది: ${locationText}`,
            timestamp: new Date().toLocaleString('en-IN', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            }),
            sender: 'user' as const
          };
          setChatMessages(prev => [...prev, locationMessage]);
          
          // Show photo upload prompt after location is shared
          setTimeout(() => {
            showPhotoUploadRequest();
          }, 1000);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert(currentLanguage === 'en' 
            ? 'Unable to get your location. Please try again.' 
            : 'మీ స్థానాన్ని పొందలేకపోయాము. దయచేసి మళ్లీ ప్రయత్నించండి.');
        }
      );
    } else {
      alert(currentLanguage === 'en' 
        ? 'Geolocation is not supported by your browser.' 
        : 'మీ బ్రౌజర్ జియోలొకేషన్‌ను మద్దతు ఇవ్వదు.');
    }
  };

  const showPhotoUploadRequest = () => {
    const photoRequestMessage = {
      text: currentLanguage === 'en'
        ? 'Please share photos related to request'
        : 'దయచేసి అభ్యర్థనకు సంబంధించిన ఫోటోలను భాగస్వామ్యం చేయండి',
      timestamp: new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }),
      sender: 'bot' as const
    };
    setChatMessages(prev => [...prev, photoRequestMessage]);
    setShowPhotoUploadPrompt(true);
  };

  // Location is mandatory for chatbot ticket creation; no "skip location" path.

  const handleSelectPhotos = () => {
    // Open file input for photo selection
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e: any) => {
      if (e.target.files && e.target.files.length > 0) {
        const files = Array.from(e.target.files) as File[];
        setSelectedFiles(files);
        
        // Create preview URLs for images
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        const imagePromises = imageFiles.map(file => {
          return new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve(e.target?.result as string);
            };
            reader.readAsDataURL(file);
          });
        });
        
        Promise.all(imagePromises).then((urls) => {
          // Add photos as user message
          const photoMessage = {
            text: currentLanguage === 'en'
              ? `Uploaded ${files.length} photo(s)`
              : `${files.length} ఫోటో(లు) అప్‌లోడ్ చేయబడ్డాయి`,
            timestamp: new Date().toLocaleString('en-IN', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            }),
            sender: 'user' as const,
            images: urls
          };
          setChatMessages(prev => [...prev, photoMessage]);
          setShowPhotoUploadPrompt(false);
          
          // Show confirmation form after photos are uploaded
          setTimeout(() => {
            showConfirmationRequest();
          }, 1000);
        });
      }
    };
    input.click();
  };

  const handleSkipUpload = () => {
    setShowPhotoUploadPrompt(false);
    
    // Add skip confirmation message
    const skipMessage = {
      text: currentLanguage === 'en'
        ? 'Upload skipped'
        : 'అప్‌లోడ్ దాటవేయబడింది',
      timestamp: new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }),
      sender: 'user' as const
    };
    setChatMessages(prev => [...prev, skipMessage]);
    
    // Show confirmation form after upload is skipped
    setTimeout(() => {
      showConfirmationRequest();
    }, 1000);
  };

  const showConfirmationRequest = async () => {
    // Generate captcha
    generateCaptcha();
    
    // Add bot message asking for confirmation
    const confirmationMessage = {
      text: currentLanguage === 'en'
        ? 'Please confirm your details'
        : 'దయచేసి మీ వివరాలను నిర్ధారించండి',
      timestamp: new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }),
      sender: 'bot' as const
    };
    setChatMessages(prev => [...prev, confirmationMessage]);
    setShowConfirmationForm(true);
  };

  const generateCaptcha = async () => {
    setIsLoadingCaptcha(true);
    try {
      const response: any = await service.get('/auth2/ss/login/capcha');
      
      if (response && response.status === 200 && response.data) {
        setCaptchaId(response.data.cptch_id);
        setCaptchaImage(response.data.cptch_img_src_tx);
        // Clear previous input
        setCaptchaInput('');
      } else {
        console.error('Failed to fetch captcha:', response?.message);
        alert(currentLanguage === 'en' 
          ? 'Failed to load captcha. Please try again.' 
          : 'క్యాప్చాను లోడ్ చేయడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.');
      }
    } catch (err: any) {
      console.error('Error fetching captcha:', err);
      alert(currentLanguage === 'en' 
        ? 'Error loading captcha. Please try again.' 
        : 'క్యాప్చాను లోడ్ చేయడంలో లోపం. దయచేసి మళ్లీ ప్రయత్నించండి.');
    } finally {
      setIsLoadingCaptcha(false);
    }
  };

  const handleConfirmYes = async () => {
    if (!captchaInput || !captchaInput.trim()) {
      alert(currentLanguage === 'en' 
        ? 'Please enter captcha' 
        : 'దయచేసి క్యాప్చాను నమోదు చేయండి');
      return;
    }

    if (!captchaId) {
      alert(currentLanguage === 'en' 
        ? 'Captcha not loaded. Please refresh and try again.' 
        : 'క్యాప్చా లోడ్ కాలేదు. దయచేసి రిఫ్రెష్ చేసి మళ్లీ ప్రయత్నించండి.');
      generateCaptcha();
      return;
    }

    // Validate ward and sachivalayam (location-based only)
    if (!locationWard?.wrd_id || !locationSachivalayam?.svm_id) {
      alert(currentLanguage === 'en'
        ? 'Please share your location again to detect Ward & Sachivalayam.'
        : 'వార్డ్ & సచివాలయం గుర్తించడానికి దయచేసి మీ స్థానాన్ని మళ్లీ పంచుకోండి.');
      setShowConfirmationForm(false);
      setShowLocationPrompt(true);
      return;
    }

    setIsSubmittingTicket(true);

    try {
      // Convert selectedFiles to base64 attachments - MUST match controller validation schema
      const attachments: Array<{ 
        base64: string; 
        fl_name: string; 
        fl_type: string; 
        cht_dt: string; 
        cht_tm: string; 
        lat?: number; 
        lng?: number; 
      }> = [];
      
      if (selectedFiles.length > 0) {
        // Format date and time as required by controller
        const currentDate = new Date();
        const day = String(currentDate.getDate()).padStart(2, '0');
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const year = currentDate.getFullYear();
        const cht_dt = `${year}-${month}-${day}`;
        
        // Time format: hh:mm a (e.g., "05:27 pm")
        const hours = currentDate.getHours();
        const minutes = String(currentDate.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'pm' : 'am';
        const hours12 = hours % 12 || 12;
        const cht_tm = `${String(hours12).padStart(2, '0')}:${minutes} ${ampm}`;
        
        const attachmentPromises = selectedFiles.map(file => {
          return new Promise<{ 
            base64: string; 
            fl_name: string; 
            fl_type: string; 
            cht_dt: string; 
            cht_tm: string; 
            lat?: number; 
            lng?: number; 
          }>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const base64 = e.target?.result as string;
              
              const attachment: {
                base64: string;
                fl_name: string;
                fl_type: string;
                cht_dt: string;
                cht_tm: string;
                lat?: number;
                lng?: number;
              } = {
                base64: base64,
                fl_name: file.name,
                fl_type: file.type,
                cht_dt: cht_dt,
                cht_tm: cht_tm
              };
              
              // Only add lat/lng if they have valid values
              if (latitude !== null && latitude !== undefined && latitude !== 0) {
                attachment.lat = latitude;
              }
              if (longitude !== null && longitude !== undefined && longitude !== 0) {
                attachment.lng = longitude;
              }
              
              resolve(attachment);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        });

        const attchmnts = await Promise.all(attachmentPromises);
        attachments.push(...attchmnts);
      }

      const finalWrdId = locationWard?.wrd_id || 0;
      const finalSvmId = locationSachivalayam?.svm_id || 0;

      if (!finalWrdId || !finalSvmId) {
        alert(currentLanguage === 'en'
          ? 'Could not detect Ward/Sachivalayam from location. Please share location again.'
          : 'స్థానం ద్వారా వార్డ్/సచివాలయం గుర్తించలేకపోయాము. దయచేసి మళ్లీ స్థానాన్ని పంచుకోండి.');
        setIsSubmittingTicket(false);
        setShowConfirmationForm(false);
        setShowLocationPrompt(true);
        return;
      }

      // Prepare ticket data
      const ticketData = {
        atchmnts: attachments,
        cmnt: userProblem || '',
        ctgry_id: selectedCategory?.categoryId || 0,
        priority: 3,
        status: 5,
        lat: latitude || 0,
        lng: longitude || 0,
        subcat: selectedCategory?.subCategoryId || 0,
        wrdnum: finalWrdId,
        svmid: finalSvmId,
        captchaID: captchaId,
        captcha: captchaInput.trim()
      };

      // Wrap in data object as expected by controller
      const postData = {
        data: ticketData
      };

      // Submit ticket
      const response: any = await service.post('/support/chatbot/tickets', postData);

      if (response && response.status === 200 && response.data) {
        const ticketData = response.data;
        const ticketNumber = ticketData.tckt_num || ticketData.tckt_id || 'N/A';
        const assignedUser = ticketData.asgn_usrid && ticketData.asgn_usrid.length > 0 
          ? ticketData.asgn_usrid[0].fst_nm 
          : '';
        const assignedUserMobile = ticketData.asgn_usrid && ticketData.asgn_usrid.length > 0 
          ? ticketData.asgn_usrid[0].mble_ph 
          : '';

        // Add success message to chat
        let successMessage = '';
        if (assignedUser) {
          successMessage = currentLanguage === 'en'
            ? `Your ticket is assigned to ${assignedUser}${assignedUserMobile ? ` -(${assignedUserMobile})` : ''} and for further help, Please find the Ticket reference No. ${ticketNumber}, Our support team will reach you soon. You can find these tickets under View Tickets`
            : `మీ టికెట్ ${assignedUser}${assignedUserMobile ? ` -(${assignedUserMobile})` : ''}కి కేటాయించబడింది మరియు మరింత సహాయం కోసం, దయచేసి టికెట్ రిఫరెన్స్ నంబర్ ${ticketNumber}ని కనుగొనండి, మా మద్దతు బృందం త్వరలో మీకు చేరుకుంటుంది. మీరు ఈ టికెట్లను వ్యూ టికెట్స్ కింద కనుగొనవచ్చు`;
        } else {
          successMessage = currentLanguage === 'en'
            ? `For further help, Please find the Ticket reference No. ${ticketNumber}, Our support team will reach you soon. You can find these tickets under View Tickets`
            : `మరింత సహాయం కోసం, దయచేసి టికెట్ రిఫరెన్స్ నంబర్ ${ticketNumber}ని కనుగొనండి, మా మద్దతు బృందం త్వరలో మీకు చేరుకుంటుంది. మీరు ఈ టికెట్లను వ్యూ టికెట్స్ కింద కనుగొనవచ్చు`;
        }

        const botSuccessMessage = {
          text: successMessage,
          timestamp: new Date().toLocaleString('en-IN', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          sender: 'bot' as const
        };
        setChatMessages(prev => [...prev, botSuccessMessage]);

        // Close confirmation form
        setShowConfirmationForm(false);

        // Show green success popup
        setCreatedTicketNumber(ticketNumber);
        setShowSuccessPopup(true);
      } else {
        throw new Error(response?.message || 'Failed to submit ticket');
      }
    } catch (err: any) {
      console.error('Error submitting ticket:', err);
      
      // Reset captcha on error
      await generateCaptcha();
      setCaptchaInput('');
      
      alert(err?.message || (currentLanguage === 'en' 
        ? 'Failed to submit ticket. Please try again.' 
        : 'టికెట్ సమర్పించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  const handleConfirmNo = () => {
    setShowConfirmationForm(false);
    setLocationWard(null);
    setLocationSachivalayam(null);
    setCaptchaInput('');
    generateCaptcha();
  };

  const handleCloseClick = () => {
    setShowExitConfirmation(true);
  };

  const closeChatDrawer = () => {
    onClose();
    setSelectedCategory(null);
    setChatMessages([]);
    setMessageInput('');
    setShowLocationPrompt(false);
    setShowPhotoUploadPrompt(false);
    setSelectedFiles([]);
    setShowConfirmationForm(false);
    setLocationWard(null);
    setLocationSachivalayam(null);
    setCaptchaInput('');
    setUserProblem('');
    setShowExitConfirmation(false);
    setShowPhotoShareModal(false);
    setPhotoShareFiles([]);
    setPhotoSharePreviewUrls([]);
    setPhotoDescription('');
    setLatitude(null);
    setLongitude(null);
  };

  const handleExitYes = () => {
    closeChatDrawer();
  };

  const handleExitNo = () => {
    setShowExitConfirmation(false);
  };

  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);
    setCreatedTicketNumber('');
    closeChatDrawer();
    // Notify parent that ticket was created successfully
    if (onTicketCreated) {
      onTicketCreated();
    }
  };

  const getUserName = () => {
    return localStorage.getItem('citizenName') || 'User';
  };

  const handlePhotoShareFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setPhotoShareFiles(files);
      
      // Create preview URLs for images
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      const imagePromises = imageFiles.map(file => {
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve(e.target?.result as string);
          };
          reader.readAsDataURL(file);
        });
      });
      
      Promise.all(imagePromises).then((urls) => {
        setPhotoSharePreviewUrls(urls);
      });
    }
  };

  const handleSubmitPhotos = async () => {
    if (photoShareFiles.length === 0) {
      alert(currentLanguage === 'en' 
        ? 'Please upload at least one photo' 
        : 'దయచేసి కనీసం ఒక ఫోటోను అప్‌లోడ్ చేయండి');
      return;
    }
    
    if (!photoDescription.trim()) {
      alert(currentLanguage === 'en' 
        ? 'Please describe your photo' 
        : 'దయచేసి మీ ఫోటోను వివరించండి');
      return;
    }

    setIsSubmittingPhotos(true);

    try {
      // Get citizen ID from localStorage
      const citizenId = localStorage.getItem('citizenId') || sessionStorage.getItem('citizenId');
      const citizenName = localStorage.getItem('citizenName') || 'Anonymous';

      // Convert files to base64 for attachments
      const attachmentPromises = photoShareFiles.map(file => {
        return new Promise<{ name: string; data: string; type: string }>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const base64 = (e.target?.result as string).split(',')[1];
            resolve({
              name: file.name,
              data: base64,
              type: file.type
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const attachments = await Promise.all(attachmentPromises);

      // Photo share data structure matching the API requirements
      const photoshareData = {
        name: citizenName,
        ctzn_id: citizenId ? parseInt(citizenId) : 0,
        prty_id: 1,
        lng_dscn_tx: photoDescription,
        atchmnts: attachments,
        tckt_type_id: 4,
        tckt_ctgry_id: 35,
        sprt_usr_id: 1581,
        sprt_tm_id: 875,
        tckt_sb_ctgry_id: 0,
        tckt_stts_id: 13,
        tkt_srce_id: 11,
        chnge_tx: '',
        ctzn_in: 1
      };

      const token = localStorage.getItem('token') || 
                   localStorage.getItem('x-access-token') || 
                   sessionStorage.getItem('token') || 
                   sessionStorage.getItem('x-access-token');

      const headers: HeadersInit = {};
      if (token) {
        headers['x-access-token'] = token;
      }

      const response = await service.post('/public/web/city-associate/photo-share/tickets', photoshareData, {
        headers
      });

      if (response.status === 200) {
        // Add success message to chat
        const fileNames = photoShareFiles.map(f => f.name).join(', ');
        const successMessage = {
          text: currentLanguage === 'en'
            ? `Uploaded ${photoShareFiles.length} file(s): ${fileNames}`
            : `${photoShareFiles.length} ఫైల్(లు) అప్‌లోడ్ చేయబడ్డాయి: ${fileNames}`,
          timestamp: new Date().toLocaleString('en-IN', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          sender: 'user' as const,
          images: photoSharePreviewUrls.length > 0 ? photoSharePreviewUrls : undefined
        };
        setChatMessages(prev => [...prev, successMessage]);
        
        // Close modal and reset
        setShowPhotoShareModal(false);
        setPhotoShareFiles([]);
        setPhotoSharePreviewUrls([]);
        setPhotoDescription('');
        
        // Show confirmation form after photos are submitted
        setTimeout(() => {
          showConfirmationRequest();
        }, 1000);
      } else {
        throw new Error('Failed to submit photos');
      }
    } catch (err: any) {
      console.error('Error submitting photos:', err);
      alert(currentLanguage === 'en' 
        ? 'Failed to submit photos. Please try again.' 
        : 'ఫోటోలను సమర్పించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.');
    } finally {
      setIsSubmittingPhotos(false);
    }
  };

  const handleCancelPhotoShare = () => {
    setShowPhotoShareModal(false);
    setPhotoShareFiles([]);
    setPhotoSharePreviewUrls([]);
    setPhotoDescription('');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      setPhotoShareFiles(files);
      
      // Create preview URLs for images
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      const imagePromises = imageFiles.map(file => {
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve(e.target?.result as string);
          };
          reader.readAsDataURL(file);
        });
      });
      
      Promise.all(imagePromises).then((urls) => {
        setPhotoSharePreviewUrls(urls);
      });
    }
  };

  if (!selectedCategory) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[10000]"
            onClick={handleCloseClick}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[10000] shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white px-5 py-4 flex items-center justify-between shadow">
              <h3 className="text-white font-bold text-lg">
                RMC SEVAK
              </h3>
              <button
                onClick={handleCloseClick}
                className="text-white hover:text-gray-100 transition p-1"
                aria-label="Close chat"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Ticket Details Card */}
            <div className="bg-gray-100 px-5 py-4 border-b border-gray-200">
              <div className="bg-white rounded-lg p-4 space-y-2 shadow-sm">
                <div className="flex items-start">
                  <span className="text-sm font-semibold text-gray-600 w-24">Ticket No:</span>
                  <span className="text-sm text-indigo-600 font-medium">New</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-semibold text-gray-600 w-24">User:</span>
                  <span className="text-sm text-gray-800 font-medium">{getUserName()}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-semibold text-gray-600 w-24">Category:</span>
                  <span className="text-sm text-gray-800 font-medium">{selectedCategory.categoryName}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-semibold text-gray-600 w-24">Sub-Category:</span>
                  <span className="text-sm text-gray-800 font-medium">{selectedCategory.subCategoryName}</span>
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-4">
              {chatMessages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-start gap-3 ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.sender === 'bot' && (
                    <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-smile-line text-white text-sm"></i>
                    </div>
                  )}
                  <div className={`max-w-[75%] ${message.sender === 'user' ? 'order-2' : ''}`}>
                    {message.sender === 'bot' && (
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-indigo-600">RMC SEVAK</span>
                      </div>
                    )}
                    <div className={`rounded-lg px-4 py-2 ${
                      message.sender === 'bot' 
                        ? 'bg-white border border-gray-200 shadow-sm' 
                        : 'bg-indigo-600 text-white'
                    }`}>
                      {message.images && message.images.length > 0 && (
                        <div className="mb-2 space-y-2">
                          {message.images.map((imageUrl, imgIndex) => (
                            <div key={imgIndex} className="rounded-lg overflow-hidden">
                              <img
                                src={imageUrl}
                                alt={`Uploaded ${imgIndex + 1}`}
                                className="max-w-full h-auto max-h-64 object-contain rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => {
                                  const newWindow = window.open();
                                  if (newWindow) {
                                    newWindow.document.write(`<img src="${imageUrl}" style="max-width: 100%; height: auto;" />`);
                                  }
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      <p className={`text-sm ${message.images && message.images.length > 0 ? 'mt-2' : ''}`}>{message.text}</p>
                    </div>
                    <p className={`text-xs text-gray-500 mt-1 ${message.sender === 'user' ? 'text-right' : 'text-left'}`}>
                      {message.timestamp}
                    </p>
                  </div>
                  {message.sender === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-line text-white text-sm"></i>
                    </div>
                  )}
                </motion.div>
              ))}

              {/* Location Sharing Prompt */}
              {showLocationPrompt && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  <div className="bg-white rounded-lg border-2 border-red-300 shadow-md p-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                          <i className="ri-map-pin-line text-2xl text-red-600"></i>
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">
                          {currentLanguage === 'en' 
                            ? 'Please share the location of the problem/work area' 
                            : 'దయచేసి సమస్య/పని ప్రాంతం యొక్క స్థానాన్ని భాగస్వామ్యం చేయండి'}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Photo Upload Prompt */}
              {showPhotoUploadPrompt && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <p className="text-sm text-gray-800 mb-2">
                          {currentLanguage === 'en' 
                            ? 'Please share the photos of the problem area by clicking here' 
                            : 'దయచేసి ఇక్కడ క్లిక్ చేయడం ద్వారా సమస్య ప్రాంతం యొక్క ఫోటోలను భాగస్వామ్యం చేయండి'}
                        </p>
                      </div>
                      <div className="flex-shrink-0 flex flex-col items-center gap-2">
                        <div 
                          onClick={handleSelectPhotos}
                          className="cursor-pointer flex flex-col items-center gap-2"
                        >
                          <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-blue-500 rounded-lg flex items-center justify-center shadow-md hover:shadow-lg transition-shadow">
                            <i className="ri-file-upload-line text-2xl text-white"></i>
                          </div>
                          <span className="text-xs font-medium text-gray-700 text-center">
                            {currentLanguage === 'en' ? 'Select Photos/Documents' : 'ఫోటోలు/పత్రాలు ఎంచుకోండి'}
                          </span>
                        </div>
                        <motion.button
                          onClick={handleSkipUpload}
                          className="px-4 py-2 bg-white border border-indigo-300 text-indigo-600 rounded-lg text-xs font-medium hover:bg-indigo-50 transition-colors"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {currentLanguage === 'en' ? 'Skip Upload' : 'అప్‌లోడ్ దాటవేయి'}
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Confirmation Form */}
              {showConfirmationForm && selectedCategory && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* Bot message with confirmation request */}
                  <div className="flex items-start gap-3 justify-start">
                    <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-smile-line text-white text-sm"></i>
                    </div>
                    <div className="max-w-[75%]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-indigo-600">RMC SEVAK</span>
                      </div>
                      <div className="bg-white border border-gray-200 shadow-sm rounded-lg px-4 py-2">
                        <p className="text-sm mb-3">{currentLanguage === 'en' ? 'Please confirm your details' : 'దయచేసి మీ వివరాలను నిర్ధారించండి'}</p>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-start">
                            <span className="text-gray-600 font-medium w-24">Category:</span>
                            <span className="text-indigo-600">{selectedCategory.categoryName}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="text-gray-600 font-medium w-24">Sub-category:</span>
                            <span className="text-indigo-600">{selectedCategory.subCategoryName}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="text-gray-600 font-medium w-24">Your Problem:</span>
                            <span className="text-indigo-600 underline">{userProblem || 'NA'}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="text-gray-600 font-medium w-24">Ward:</span>
                            <span className="text-indigo-600">
                              {locationWard?.wrd_nu ? `W${locationWard.wrd_nu}` : (locationWard?.wrd_nm || 'NA')}
                            </span>
                          </div>
                          <div className="flex items-start">
                            <span className="text-gray-600 font-medium w-24">Sachivalayam:</span>
                            <span className="text-indigo-600">
                              {locationSachivalayam?.svm_nm || 'NA'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-left">
                        {new Date().toLocaleString('en-IN', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric', 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: true 
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Confirmation Form Card */}
                  <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                    <h3 className="text-red-600 font-bold text-lg mb-4">
                      {currentLanguage === 'en' ? 'Please confirm your details' : 'దయచేసి మీ వివరాలను నిర్ధారించండి'}
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <span className="text-sm font-semibold text-gray-600 w-28">Category :</span>
                        <span className="text-sm text-indigo-600 font-medium">{selectedCategory.categoryName}</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-sm font-semibold text-gray-600 w-28">Sub-category :</span>
                        <span className="text-sm text-indigo-600 font-medium">{selectedCategory.subCategoryName}</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-sm font-semibold text-gray-600 w-28">Your Problem :</span>
                        <span className="text-sm text-indigo-600 font-medium">{userProblem || 'NA'}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm font-semibold text-gray-600 w-28">Ward :</span>
                        <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-indigo-700 font-medium">
                          {locationWard?.wrd_nu ? `W${locationWard.wrd_nu}` : (locationWard?.wrd_nm || 'NA')}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm font-semibold text-gray-600 w-28">Sachivalayam :</span>
                        <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-indigo-700 font-medium">
                          {locationSachivalayam?.svm_nm || 'NA'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex-1 bg-gray-100 p-3 rounded-lg border border-gray-300 min-h-[60px] flex items-center justify-center">
                          {isLoadingCaptcha ? (
                            <div className="flex items-center justify-center">
                              <i className="ri-loader-4-line animate-spin text-2xl text-gray-600"></i>
                            </div>
                          ) : captchaImage ? (
                            <img 
                              src={captchaImage} 
                              alt="Captcha" 
                              className="max-w-full h-auto"
                              style={{ maxHeight: '50px' }}
                            />
                          ) : (
                            <div className="text-sm text-gray-500 text-center">
                              {currentLanguage === 'en' ? 'Click refresh to load captcha' : 'క్యాప్చాను లోడ్ చేయడానికి రిఫ్రెష్ క్లిక్ చేయండి'}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={generateCaptcha}
                          disabled={isLoadingCaptcha}
                          className="w-10 h-10 bg-purple-500 hover:bg-purple-600 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={currentLanguage === 'en' ? 'Refresh Captcha' : 'క్యాప్చాను రిఫ్రెష్ చేయండి'}
                        >
                          <i className={`ri-refresh-line text-lg ${isLoadingCaptcha ? 'animate-spin' : ''}`}></i>
                        </button>
                      </div>
                      <input
                        type="text"
                        value={captchaInput}
                        onChange={(e) => setCaptchaInput(e.target.value)}
                        placeholder={currentLanguage === 'en' ? 'Enter the code from the above image' : 'పై చిత్రం నుండి కోడ్‌ను నమోదు చేయండి'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      />
                    </div>
                    
                    <div className="flex gap-3 mt-4">
                      <motion.button
                        onClick={handleConfirmYes}
                        disabled={isSubmittingTicket}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        whileHover={{ scale: isSubmittingTicket ? 1 : 1.02 }}
                        whileTap={{ scale: isSubmittingTicket ? 1 : 0.98 }}
                      >
                        {isSubmittingTicket ? (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-loader-4-line animate-spin"></i>
                            {currentLanguage === 'en' ? 'Submitting...' : 'సమర్పిస్తోంది...'}
                          </span>
                        ) : (
                          currentLanguage === 'en' ? 'Yes' : 'అవును'
                        )}
                      </motion.button>
                      <motion.button
                        onClick={handleConfirmNo}
                        className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-lg transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {currentLanguage === 'en' ? 'No' : 'కాదు'}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Message Input */}
            <div className="border-t border-gray-200 bg-white p-4">
              {selectedFiles.length > 0 && (
                <div className="mb-2 text-xs text-gray-600">
                  {currentLanguage === 'en' 
                    ? `${selectedFiles.length} file(s) ready to upload` 
                    : `${selectedFiles.length} ఫైల్(లు) అప్‌లోడ్ చేయడానికి సిద్ధంగా ఉన్నాయి`}
                </div>
              )}
              <div className="flex items-center gap-2">
                {showLocationPrompt && (
                  <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    onClick={handleShareLocation}
                    className="w-12 h-12 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors shadow-md"
                    title={currentLanguage === 'en' ? 'Share Location' : 'స్థానాన్ని భాగస్వామ్యం చేయండి'}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <i className="ri-map-pin-line text-xl"></i>
                  </motion.button>
                )}
                <input
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSendMessage();
                    }
                  }}
                  placeholder={currentLanguage === 'en' ? 'Report your problem...' : 'మీ సమస్యను నివేదించండి...'}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim()}
                  className="w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                >
                  <i className="ri-send-plane-fill text-xl"></i>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Exit Confirmation Modal */}
          <AnimatePresence>
            {showExitConfirmation && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-[10001]"
                  onClick={handleExitNo}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                  className="fixed inset-0 flex items-center justify-center z-[10001] pointer-events-none"
                >
                  <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full mx-4 pointer-events-auto">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                          <span className="text-white text-2xl font-bold">?</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-800 text-base font-medium">
                          {currentLanguage === 'en' 
                            ? 'On exiting, all the chat history will be cleared' 
                            : 'బయటకు వెళ్లడంతో, అన్ని చాట్ చరిత్ర క్లియర్ చేయబడుతుంది'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-6">
                      <motion.button
                        onClick={handleExitNo}
                        className="flex-1 px-4 py-2 border-2 border-indigo-300 text-indigo-600 font-semibold rounded-lg hover:bg-indigo-50 transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {currentLanguage === 'en' ? 'No' : 'కాదు'}
                      </motion.button>
                      <motion.button
                        onClick={handleExitYes}
                        className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {currentLanguage === 'en' ? 'Yes' : 'అవును'}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Success Popup Modal */}
          <AnimatePresence>
            {showSuccessPopup && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-[10002]"
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                  className="fixed inset-0 flex items-center justify-center z-[10002] pointer-events-none"
                >
                  <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full mx-4 pointer-events-auto">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-3xl"></i>
                      </div>
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-green-600 mb-2">
                          {currentLanguage === 'en' ? 'Ticket Created Successfully!' : 'టికెట్ విజయవంతంగా సృష్టించబడింది!'}
                        </h3>
                        <p className="text-gray-700 text-base">
                          {currentLanguage === 'en'
                            ? `Ticket No: ${createdTicketNumber}`
                            : `టికెట్ నంబర్: ${createdTicketNumber}`}
                        </p>
                      </div>
                    </div>
                    <div className="mt-6">
                      <motion.button
                        onClick={handleSuccessPopupClose}
                        className="w-full px-4 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {currentLanguage === 'en' ? 'OK' : 'సరే'}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}

